
import { Button, Input, PillGroup } from '@/components/ui';
import { PrayerFormRecap } from './PrayerFormRecap';
import { PrayerFormStepNav } from './PrayerFormStepNav';
import type { SubmitterGender } from '@/types';
import type { PrayerFormData, ValidationErrors } from '@/lib/validation';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

interface PrayerFormStepConnectProps {
  formData: PrayerFormData;
  errors: ValidationErrors;
  isSubmitting: boolean;
  submitError: string | null;
  onFieldChange: <K extends keyof PrayerFormData>(field: K, value: PrayerFormData[K]) => void;
  onBack: () => void;
}

export function PrayerFormStepConnect({
  formData,
  errors,
  isSubmitting,
  submitError,
  onFieldChange,
  onBack,
}: PrayerFormStepConnectProps) {
  return (
    <div className="space-y-8">
      <PrayerFormRecap formData={formData} />

      <div className="space-y-6">
        <Input
          id="prayer-email"
          type="email"
          label="Reach me at"
          variant="minimal"
          placeholder="your@email.com"
          value={formData.email}
          onChange={(e) => onFieldChange('email', e.target.value)}
          error={errors.email}
          autoComplete="email"
          required
        />

        <Input
          id="prayer-phone"
          type="tel"
          label="Phone (optional)"
          variant="minimal"
          placeholder="(555) 123-4567"
          value={formData.phone}
          onChange={(e) => onFieldChange('phone', e.target.value)}
          error={errors.phone}
          autoComplete="tel"
        />
      </div>

      <PillGroup
        name="gender"
        label="Gender"
        helperText="Helps us pair you with a prayer partner for personal follow-up."
        options={GENDER_OPTIONS}
        value={formData.gender}
        onChange={(value) => onFieldChange('gender', value as SubmitterGender)}
        error={errors.gender}
        required
      />

      <div aria-live="polite" role="alert">
        {submitError && (
          <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {submitError}
            </p>
          </div>
        )}
      </div>

      <PrayerFormStepNav onPrevious={onBack} previousDisabled={isSubmitting}>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending…' : 'Send'}
        </Button>
      </PrayerFormStepNav>
    </div>
  );
}
